import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { CONTACT_INFO } from "@/data/content";
import { Mail, Phone, MapPin } from "lucide-react";
import BrandMarkWatermark from "@/components/BrandMarkWatermark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: t.contact.form.interest_options[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        interest: form.interest || null,
        message: form.message.trim(),
        language: lang,
      });
      toast.success(t.contact.form.success);
      setForm((f) => ({ ...f, name: "", email: "", phone: "", message: "" }));
    } catch (err) {
      toast.error(t.contact.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative overflow-hidden section-y bg-[color:var(--anthracite)] text-[color:var(--bone)]"
      data-testid="contact-section"
    >
      <BrandMarkWatermark size={820} tone="light" opacity={0.09} style={{ bottom: -140, left: -140, zIndex: 1 }} />
      <div className="container-lux relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left — copy + info */}
          <div className="lg:col-span-5">
            <div className="overline" style={{ color: "var(--bronze)" }} data-testid="contact-overline">
              {t.contact.overline}
            </div>
            <h2
              className="mt-6 font-serif-display text-[clamp(2rem,3.8vw,3.25rem)] leading-[1.05] tracking-[-0.01em]"
              data-testid="contact-title"
            >
              {t.contact.title}
            </h2>
            <p className="mt-6 text-[15px] leading-[1.75] text-[color:var(--bone)]/70 max-w-md">
              {t.contact.lede}
            </p>

            <div className="mt-12 space-y-8">
              <div className="flex items-start gap-4" data-testid="contact-email">
                <Mail size={18} strokeWidth={1} className="text-[color:var(--bronze)] mt-1 flex-none" />
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
                    {t.contact.email_label}
                  </div>
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="font-serif-display text-[20px] hover:text-[color:var(--bronze)] transition-colors"
                  >
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-phones">
                <Phone size={18} strokeWidth={1} className="text-[color:var(--bronze)] mt-1 flex-none" />
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
                    {t.contact.phone_label}
                  </div>
                  {CONTACT_INFO.phones.map((p) => (
                    <div key={p.number} className="font-serif-display text-[18px]">
                      <span className="text-[color:var(--bronze)] me-2">{p.label} ·</span>
                      <a
                        href={`tel:${p.number.replace(/\s/g, "")}`}
                        className="hover:text-[color:var(--bronze)] transition-colors"
                      >
                        {p.number}
                      </a>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-address">
                <MapPin size={18} strokeWidth={1} className="text-[color:var(--bronze)] mt-1 flex-none" />
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
                    {t.contact.address_title}
                  </div>
                  <div className="font-serif-display text-[17px] leading-[1.5] max-w-sm mt-1">
                    {t.contact.address}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="bg-[color:var(--charcoal)] p-8 md:p-12 border border-[color:var(--copper)]/20"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <label className="field">
                  <span className="field-label" style={{ color: "rgba(253,251,247,0.55)" }}>
                    {t.contact.form.name}
                  </span>
                  <input
                    className="field-input"
                    style={{ color: "var(--bone)", borderBottomColor: "rgba(253,251,247,0.2)" }}
                    value={form.name}
                    onChange={update("name")}
                    required
                    data-testid="contact-input-name"
                  />
                </label>

                <label className="field">
                  <span className="field-label" style={{ color: "rgba(253,251,247,0.55)" }}>
                    {t.contact.form.email}
                  </span>
                  <input
                    type="email"
                    className="field-input"
                    style={{ color: "var(--bone)", borderBottomColor: "rgba(253,251,247,0.2)" }}
                    value={form.email}
                    onChange={update("email")}
                    required
                    data-testid="contact-input-email"
                  />
                </label>

                <label className="field">
                  <span className="field-label" style={{ color: "rgba(253,251,247,0.55)" }}>
                    {t.contact.form.phone}
                  </span>
                  <input
                    className="field-input"
                    style={{ color: "var(--bone)", borderBottomColor: "rgba(253,251,247,0.2)" }}
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="contact-input-phone"
                  />
                </label>

                <label className="field">
                  <span className="field-label" style={{ color: "rgba(253,251,247,0.55)" }}>
                    {t.contact.form.interest}
                  </span>
                  <select
                    className="field-select"
                    style={{
                      color: "var(--bone)",
                      borderBottomColor: "rgba(253,251,247,0.2)",
                      backgroundImage:
                        "linear-gradient(45deg, transparent 50%, #FDFBF7 50%), linear-gradient(-45deg, transparent 50%, #FDFBF7 50%)",
                    }}
                    value={form.interest}
                    onChange={update("interest")}
                    data-testid="contact-input-interest"
                  >
                    {t.contact.form.interest_options.map((opt) => (
                      <option key={opt} value={opt} style={{ color: "#1A1A1A" }}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="field md:col-span-2">
                  <span className="field-label" style={{ color: "rgba(253,251,247,0.55)" }}>
                    {t.contact.form.message}
                  </span>
                  <textarea
                    className="field-textarea"
                    style={{ color: "var(--bone)", borderBottomColor: "rgba(253,251,247,0.2)" }}
                    value={form.message}
                    onChange={update("message")}
                    required
                    rows={5}
                    data-testid="contact-input-message"
                  />
                </label>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary mt-10 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: "var(--bone)", color: "var(--anthracite)", borderColor: "var(--bone)" }}
                data-testid="contact-submit-button"
              >
                {submitting ? t.contact.form.sending : t.contact.form.submit}
                <span aria-hidden>→</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
